/**
 * coco+ の「おすすめ枠」A/B（cocoplus_recommend）の群分け。
 *
 * 新規の男性ユーザーを、無料の250ptを1分のビデオ通話で使い切らせず、チャットから始めさせる検証。
 * - A群（control）: 現行どおり。さがす画面の最上部に「無料通話を体験する」カードを出す
 * - B群（treatment）: カードの代わりにおすすめ枠（メッセージ起点）を出し、AI無料通話は
 *   「今すぐビデオ通話できる女の子」の行の丸アイコンから入る
 *
 * 群は `getExperimentBucket`（実験名 + ログイン中の user_id）のバケットで決める。
 * バケット 0〜49 = A群 / 50〜99 = B群。割当は保存せず、何度計算しても同じ群になる。
 * 対象は coco+ のみ（他のアプリでは使わない）。
 */
import { COCOPLUS_RECOMMEND_AB_STARTED } from "~/constants/cocoplusRecommend";
import { getExperimentBucket } from "~/lib/abTest";
import { isCocoplusApp } from "~/lib/applicationId";
import { captureError } from "~/lib/sentry/captureError";
import type { FirebaseAbVariant } from "~/lib/tracking/firebase";

/** 実験名。ハッシュのソルトなので変更すると群が入れ替わる */
export const COCOPLUS_RECOMMEND_EXPERIMENT = "cocoplus_recommend";

/** このバケット以上が B 群（処置群）。未満は A 群（対照群） */
const TREATMENT_BUCKET_START = 50;

/** デモ通話案内の入口の見た目（計測の placement と同じ値） */
export type CocoplusRecommendPlacement = "card" | "icon";

/** 解決済みの群。ストアや通話開始処理など hook を使えない場所の計測が同期的に読むために持つ */
let resolvedVariant: { userId: string; variant: FirebaseAbVariant } | null =
  null;

/**
 * 群を解決する。バケットの算出に失敗したときは A 群（現行どおり）として扱い、
 * 実験のために既存の画面を壊さない。
 */
export const resolveCocoplusRecommendVariant = async (
  userId: string,
): Promise<FirebaseAbVariant> => {
  let variant: FirebaseAbVariant;
  // A/B 開始前は全員 B 群の画面にする（開始日に COCOPLUS_RECOMMEND_AB_STARTED を true にする）
  if (!COCOPLUS_RECOMMEND_AB_STARTED) {
    resolvedVariant = { userId, variant: "treatment" };
    return "treatment";
  }
  try {
    const bucket = await getExperimentBucket(
      COCOPLUS_RECOMMEND_EXPERIMENT,
      userId,
    );
    variant = bucket >= TREATMENT_BUCKET_START ? "treatment" : "control";
  } catch (error) {
    captureError(error, "cocoplusRecommendExperiment: バケットの算出に失敗");
    variant = "control";
  }
  resolvedVariant = { userId, variant };
  return variant;
};

/** 群に対応する入口の見た目。A=カード / B=アイコン */
export const toCocoplusRecommendPlacement = (
  variant: FirebaseAbVariant,
): CocoplusRecommendPlacement => (variant === "treatment" ? "icon" : "card");

/**
 * デモ通話の計測（demo_call_banner_step）に足す A/B の付帯情報。
 * coco+ 以外、または群が未解決のときは空（他のアプリの計測は従来どおり）。
 */
export const getCocoplusRecommendTrackingParams = (
  userId: string | null,
): {
  ab_variant?: FirebaseAbVariant;
  placement?: CocoplusRecommendPlacement;
} => {
  if (!isCocoplusApp() || !userId || resolvedVariant?.userId !== userId) {
    return {};
  }
  const { variant } = resolvedVariant;
  return {
    ab_variant: variant,
    placement: toCocoplusRecommendPlacement(variant),
  };
};

/** テスト用: 解決済みの群を捨てる */
export const resetCocoplusRecommendVariantForTest = (): void => {
  resolvedVariant = null;
};
